import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Page } from 'components/common'
import { Modal } from 'components/modal'
import { Description } from 'pages/Imali/Linking/components/Instructions/Items'
import Image from 'components/common/Image'
import Header from 'components/Header'
import NewErrorPage from './New'

import { redirect } from 'helpers/window'
import { asBoolean } from 'helpers/environment'

import { IMetaData } from 'Errors/StandardError'
interface ErrorPageProps {
    title: string
    description: string
    image: string
    redirectURL?: string
    metaData?: IMetaData
    cta?: {
        id: string
        schema_version: string
        button_text: string
    }
}

const MS_PER_SECOND = 1000

const ErrorPage = ({
    title,
    description,
    image,
    redirectURL,
    metaData,
    cta,
}: ErrorPageProps): ReactElement => {
    if (asBoolean(process.env.REACT_APP_SHOW_OLD_ERROR_PAGE)) {
        return (
            <OldErrorPage
                title={title}
                description={description}
                image={image}
                redirectURL={redirectURL}
            />
        )
    }

    return (
        <NewErrorPage
            title={title}
            description={description}
            image={image}
            redirectURL={redirectURL}
            metaData={metaData}
            cta={cta}
        />
    )
}

const OldErrorPage = ({
    title,
    description,
    image,
    redirectURL,
}: ErrorPageProps): ReactElement => {
    useEffect(() => {
        if (redirectURL) {
            setTimeout(() => {
                redirect(redirectURL)
            }, process.env.REACT_APP_REDIRECT_TO_MERCHANT_ON_ERROR_INTERVAL_IN_SECONDS * MS_PER_SECOND)
        }
    }, [])

    return (
        <>
            <Page data-testid="error-page">
                <Container>
                    <Header instrumentType="GOPAY_WALLET" border />
                    <Content>
                        <div style={{ padding: '35px 0 16px' }}>
                            <Image
                                data-testid="error-image"
                                alt="error"
                                src={image}
                                width="373px"
                                height="100%"
                                style={{ margin: '0 auto', maxWidth: '100%' }}
                            />
                        </div>
                        <h4
                            style={{ margin: '1rem 0' }}
                            data-testid="error-title"
                        >
                            {title}
                        </h4>
                        <Description data-testid="error-description">
                            {description}
                        </Description>
                    </Content>
                </Container>
                <Footer />
            </Page>
        </>
    )
}

const Container = styled.div`
    display: flex;
    flex: 0 1;
    border: 1px solid #bcc5cf;
    border-radius: 6px;
    margin: 0 auto;
    width: 480px;
    justify-content: center;
    flex-direction: column;
    @media screen and (max-width: 576px) {
        flex: 1 1;
        width: 100%;
        border: none;
    }
`

const Content = styled.div`
    padding: 3.125rem 1.5rem 1.5rem;
    flex-grow: 1;
    @media screen and (min-width: 576px) {
        padding: 3rem 2.5rem 2.25rem;
        min-height: 300px;
    }
`

const Footer = (): ReactElement => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [modalReference, setModalReference] = useState<string | undefined>()
    const [modalTitle, setModalTitle] = useState<string | undefined>()
    const { t } = useTranslation()

    const toggle = useCallback(() => {
        setIsModalOpen((isOpen) => !isOpen)
    }, [])

    const handleClick = useCallback((value: string, title: string): void => {
        if (value) {
            setModalReference(value)
            setModalTitle(title)
            toggle()
        }
    }, [])

    return (
        <>
            <List>
                <li>
                    <Link
                        onClick={() =>
                            handleClick(
                                process.env
                                    .REACT_APP_EXTERNAL_LINK_PRIVACY_POLICY,
                                t('labelPrivacyPolicy')
                            )
                        }
                    >
                        <span>{t('labelPrivacyPolicy')}</span>
                    </Link>
                </li>
                <li>
                    <Link
                        onClick={() =>
                            handleClick(
                                process.env
                                    .REACT_APP_EXTERNAL_LINK_TERMS_AND_CONDITIONS,
                                t('labelTermsAndConditions')
                            )
                        }
                    >
                        <span>{t('labelTermsAndConditions')}</span>
                    </Link>
                </li>
            </List>
            <Modal isOpen={isModalOpen} title={modalTitle} handleClose={toggle}>
                <iframe
                    src={modalReference}
                    style={{ width: '100%', height: '100%', border: 0 }}
                ></iframe>
            </Modal>
        </>
    )
}

const List = styled.ul`
    display: flex;
    justify-content: flex-end;
    list-style: none;
    padding: 16px 0;
    margin: 0;
    width: 480px;
    & li span {
        font-weight: bold;
        font-size: 14px;
    }
    & li:first-child {
        margin-right: 20px;
    }
    @media screen and (max-width: 576px) {
        padding: 24px;
        width: 100%;
    }
`

const Link = styled.a`
    font-weight: bold;
    text-decoration: none;
    cursor: pointer;
`

export default ErrorPage
